<template>
  <div
    class="carousel-projects"
    ref="carouselRef"
    :class="{ '--has-title': props.title }"
  >
    <div
      v-if="props.title"
      class="text-l-2 carousel-projects__title"
      v-html="props.title"
    />

    <WidgetSliderSwiper
      ref="swiperRef"
      :length="props.slides.length"
      :options="{
        pagination: true,
        hideButtons: true,
        clickable: true,
        slidesPerView: 'auto',
      }"
    >
      <template
        v-for="(slide, index) in props.slides"
        :key="index"
        #[`slide-${index}`]
      >
        <PagesCommonProjectItem :data="slide" />
      </template>
    </WidgetSliderSwiper>
    <div
      v-if="!touch"
      class="carousel-projects__prev-area"
      @mousemove="handleMouseMove($event, 'prev')"
      @click="handleClick($event, 'prev')"
      @mouseleave="mouseLeave"
    />
    <div
      v-if="!touch"
      class="carousel-projects__next-area"
      @mousemove="handleMouseMove($event, 'next')"
      @click="handleClick($event, 'next')"
      @mouseleave="mouseLeave"
    />
    <div
      v-if="!touch"
      ref="cursorRef"
      class="carousel-projects__cursor"
      :class="{
        'is-left': isLeft,
        'is-right': isRight,
      }"
    >
      <svg
        id="arr-left"
        xmlns="http://www.w3.org/2000/svg"
        width="57"
        height="55"
        viewBox="0 0 57 55"
      >
        <g id="Arrow_Left" transform="translate(995.122 3485.633) rotate(180)">
          <rect
            id="Rettangolo_93"
            data-name="Rettangolo 93"
            width="57"
            height="55"
            rx="27.5"
            transform="translate(938.122 3430.633)"
            fill="#ff6d05"
          />
          <path
            id="Unione_22"
            data-name="Unione 22"
            d="M-362.777-540.031a1.5,1.5,0,0,1,0-2.121l8.975-8.975h-19.45a1.5,1.5,0,0,1-1.5-1.5,1.5,1.5,0,0,1,1.5-1.5H-353.8l-8.974-8.975a1.5,1.5,0,0,1,0-2.121,1.5,1.5,0,0,1,2.121,0l11.535,11.535q.032.032.061.065h0l.006.007a1.494,1.494,0,0,1,.371.97c0,.006,0,.013,0,.019s0,.013,0,.019a1.492,1.492,0,0,1-.327.916v0l-.008.01,0,.006,0,0q-.045.054-.094.1l-11.535,11.535a1.5,1.5,0,0,1-1.061.44A1.493,1.493,0,0,1-362.777-540.031Z"
            transform="translate(1328.402 4011.112)"
            fill="#f7f1e9"
          />
        </g>
      </svg>

      <svg
        id="arr-right"
        xmlns="http://www.w3.org/2000/svg"
        width="57"
        height="55"
        viewBox="0 0 57 55"
      >
        <g id="Arrow_Right" transform="translate(-938.122 -3430.633)">
          <rect
            id="Rettangolo_93"
            data-name="Rettangolo 93"
            width="57"
            height="55"
            rx="27.5"
            transform="translate(938.122 3430.633)"
            fill="#ff6d05"
          />
          <path
            id="Unione_22"
            data-name="Unione 22"
            d="M-362.777-540.031a1.5,1.5,0,0,1,0-2.121l8.975-8.975h-19.45a1.5,1.5,0,0,1-1.5-1.5,1.5,1.5,0,0,1,1.5-1.5H-353.8l-8.974-8.975a1.5,1.5,0,0,1,0-2.121,1.5,1.5,0,0,1,2.121,0l11.535,11.535q.032.032.061.065h0l.006.007a1.494,1.494,0,0,1,.371.97c0,.006,0,.013,0,.019s0,.013,0,.019a1.492,1.492,0,0,1-.327.916v0l-.008.01,0,.006,0,0q-.045.054-.094.1l-11.535,11.535a1.5,1.5,0,0,1-1.061.44A1.493,1.493,0,0,1-362.777-540.031Z"
            transform="translate(1328.402 4011.112)"
            fill="#f7f1e9"
          />
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarouselProjects',
}
</script>

<script setup>
import { gsap } from 'gsap'
import { lerp } from '~/assets/js/utils.js'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  slides: {
    type: Array,
    default: () => [],
  },
})

const touch = ref(false)

const carouselRef = ref(null)
const cursorRef = ref(null)
const swiperRef = ref(null)
const isCursorVisible = ref(false)
const isLeft = ref(false)
const isRight = ref(false)
const targetX = ref(0)
const targetY = ref(0)
const cursorX = ref(0)
const cursorY = ref(0)
const currentMask = ref('')

const cursorSize = 32

const updateMousePosition = (event) => {
  const rect = carouselRef.value.getBoundingClientRect()
  targetX.value = event.clientX - rect.left
  targetY.value = event.clientY - rect.top
}

const handleMouseMove = (event, action) => {
  updateMousePosition(event)
  if (action === 'prev' || action === 'next') {
    if (action === 'prev') {
      isRight.value = false
      isLeft.value = true
    }
    if (action === 'next') {
      isLeft.value = false
      isRight.value = true
    }

    if (!isCursorVisible.value && cursorRef.value) {
      isCursorVisible.value = true
      gsap.to(cursorRef.value, {
        scale: 1,
        duration: 0.5,
        ease: 'power2.out',
      })
    }

    const mask =
      action === 'prev' ? 'url(#playMaskLeft)' : 'url(#playMaskRight)'
    gsap.delayedCall(0.25, () => {
      currentMask.value = mask
    })
  }
}

const handleClick = (event, action) => {
  if (action === 'prev') {
    swiperRef.value.slidePrev()
  } else if (action === 'next') {
    swiperRef.value.slideNext()
  }
}

const mouseLeave = () => {
  if (cursorRef.value) {
    gsap.to(cursorRef.value, {
      scale: 0,
      duration: 0.5,
      ease: 'power2.out',
      onComplete: () => {
        isCursorVisible.value = false
      },
    })
  }
}

const animateCursor = () => {
  cursorX.value = lerp(cursorX.value, targetX.value - cursorSize, 0.1)
  cursorY.value = lerp(cursorY.value, targetY.value - cursorSize, 0.1)

  if (cursorRef.value) {
    gsap.to(cursorRef.value, {
      x: cursorX.value,
      y: cursorY.value,
      duration: 0.1,
      ease: 'none',
    })
  }

  requestAnimationFrame(animateCursor)
}

onMounted(() => {
  touch.value = 'ontouchstart' in window || !!navigator.maxTouchPoints
  carouselRef.value.addEventListener('mousemove', handleMouseMove)
  animateCursor()
  // T-A-C-O-N
  setTimeout(() => {
    window.dispatchEvent(new Event('resize'))
  }, 1000)
})

onUnmounted(() => {
  if (carouselRef.value) {
    carouselRef.value.removeEventListener('mousemove', handleMouseMove)
  }
})
</script>

<style lang="scss">
@import './style.scss';
</style>
